import { queryClient } from "App";
import { get } from "lodash";
import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getCaseEventsTableColumns,
  getCaseEventsTableFilters,
} from "routes/case_event/CaseEventsTableColumnsAndFilters";
import { CASE_EVENTS_TABLE_CONSTANTS } from "routes/case_event/CaseEventsTableV3Constants";
import UserService from "services/User";
import { addResizedColumns } from "./useTableConfig";
import { caseEventKeys } from "routes/case_event/queryKeys";

/**
 * Custom hook for managing case events table state.
 *
 * Args:
 *     caseId: The case ID to filter events by.
 *     openMapModal: Function to open the map modal with location data.
 *
 * Returns:
 *     Object containing filters and columns for the table.
 */
export const useCaseEventsTable = ({
  caseId = null,
  productLineId = null,
  openMapModal = null,
}) => {
  const { data: tableSettings, isSuccess: isTableSettingsFetched } = useQuery({
    queryKey: ["table-settings"],
    queryFn: UserService.getUserSettings,
  });

  const queryKey = useMemo(() => caseEventKeys.list({ caseId }), [caseId]);

  const invalidateData = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey });
  }, [queryKey]);

  const filters = useMemo(() => {
    return getCaseEventsTableFilters({
      caseId,
      productLineId,
    });
  }, [caseId, productLineId]);

  const columns = useMemo(() => {
    if (!isTableSettingsFetched) return [];
    const initialColumns = getCaseEventsTableColumns({
      caseId,
      openMapModal,
    });

    const caseEventsTableSettings = get(
      tableSettings,
      `user_settings.preferences.tables.${CASE_EVENTS_TABLE_CONSTANTS.tableName}`,
      {}
    );
    const withResizedColumns = addResizedColumns({
      columns: initialColumns,
      resizedColumns: caseEventsTableSettings?.resized_columns || {},
    });

    return withResizedColumns;
  }, [caseId, tableSettings, isTableSettingsFetched, openMapModal]);

  return { filters, columns, queryKey, invalidateData };
};
